import { lazy, Suspense, type ReactNode } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { LanguageProvider } from "@/contexts/LanguageContext";
import SplashScreen from "@/components/brand/SplashScreen";
import { AppLayout } from "@/components/layout/AppLayout";
import type { NewBudgetSelection } from "@/components/layout/NewBudgetModal";
import { CookieConsentBanner } from "@/components/legal/CookieConsentBanner";
import { OrzioAssistant } from "@/components/ai/OrzioAssistant";
import { LocaleOnboardingDialog } from "@/components/onboarding/LocaleOnboardingDialog";
import { AdminGuard } from "@/modules/admin/components/AdminGuard";

const Landing = lazy(() => import("./pages/Landing"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const AnalyticsDashboard = lazy(() => import("./pages/AnalyticsDashboard"));
const BudgetWizard = lazy(() => import("./pages/BudgetWizard"));
const BudgetEditor = lazy(() => import("./pages/BudgetEditor"));
const ExecutionWorkspace = lazy(() => import("./pages/ExecutionWorkspace"));
const Settings = lazy(() => import("./pages/Settings"));
const Templates = lazy(() => import("./pages/Templates"));
const TemplateDetail = lazy(() => import("./pages/TemplateDetail"));
const BimPage = lazy(() => import("./pages/BimPage"));
const RevitTakeoffImportPage = lazy(() => import("./pages/RevitTakeoffImportPage"));
const CostDatabasePage = lazy(() => import("./pages/CostDatabasePage"));
const DocumentsPage = lazy(() => import("./pages/DocumentsPage"));
const WorksPage = lazy(() => import("./pages/WorksPage"));
const MeasurementAutosIndexPage = lazy(() => import("./pages/MeasurementAutosIndexPage"));
const ContactsPage = lazy(() => import("./pages/ContactsPage"));
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));
const BimQuantityMappingPage = lazy(() => import("./pages/BimQuantityMappingPage"));
const BudgetReviewPage = lazy(() => import("./pages/BudgetReviewPage"));
const BudgetComparisonPage = lazy(() => import("./modules/budget-comparison/components/BudgetComparisonPage"));
const MeasurementPage = lazy(() => import("./pages/MeasurementPage"));
const PriceIntelligencePage = lazy(() => import("./modules/price-intelligence/components/PriceIntelligencePage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Help = lazy(() => import("./pages/Help"));
const TermosPT = lazy(() => import("./pages/legal/TermosPT"));
const TermosBR = lazy(() => import("./pages/legal/TermosBR"));
const PrivacidadePT = lazy(() => import("./pages/legal/PrivacidadePT"));
const PrivacidadeBR = lazy(() => import("./pages/legal/PrivacidadeBR"));
const LegalCookies = lazy(() => import("./pages/legal/Cookies"));
const AdminDashboard = lazy(() => import("./modules/admin/pages/AdminDashboard"));
const AdminCatalogOverview = lazy(() => import("./modules/admin/pages/AdminCatalogOverview"));

const queryClient = new QueryClient();

const ShellRoute = ({ children, contentClassName }: { children: ReactNode; contentClassName?: string }) => {
  const navigate = useNavigate();

  const handleNewBudgetSelect = (selection: NewBudgetSelection) => {
    if (selection.intentionId === "work-template" || selection.intentionId === "project-template") {
      navigate("/templates", { state: selection });
      return;
    }

    if (selection.intentionId === "ifc-import") {
      navigate("/bim", { state: selection });
      return;
    }

    if (selection.intentionId === "blank") {
      navigate("/budget/new/edit", { state: selection });
      return;
    }

    navigate("/budget/new/wizard", { state: selection });
  };

  return (
    <AppLayout contentClassName={contentClassName} onNewBudgetSelect={handleNewBudgetSelect}>
      {children}
    </AppLayout>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LanguageProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<SplashScreen />}>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/dashboard" element={<ShellRoute contentClassName="p-0"><Dashboard /></ShellRoute>} />
              <Route path="/analytics" element={<ShellRoute contentClassName="p-0"><AnalyticsDashboard /></ShellRoute>} />
              <Route path="/budgets" element={<ShellRoute contentClassName="p-0"><Dashboard /></ShellRoute>} />
              {/* Budget creation entrypoint is /budget/new; direct edit/wizard routes stay for compatibility and power users. */}
              <Route path="/budget/new" element={<ShellRoute contentClassName="p-0"><Dashboard /></ShellRoute>} />
              <Route path="/budget/new/edit" element={<ShellRoute contentClassName="p-0"><BudgetEditor /></ShellRoute>} />
              <Route path="/budget/new/wizard" element={<BudgetWizard />} />
              <Route path="/budget/:budgetId/edit" element={<ShellRoute contentClassName="p-0"><BudgetEditor /></ShellRoute>} />
              <Route path="/budget/:budgetId/wizard" element={<BudgetWizard />} />
              <Route path="/budget/:budgetId/execution" element={<ShellRoute contentClassName="p-0"><ExecutionWorkspace /></ShellRoute>} />
              <Route path="/budget/:budgetId/mapping/:ifcImportId" element={<ShellRoute contentClassName="p-0"><BimQuantityMappingPage /></ShellRoute>} />
              <Route path="/mapping/:ifcImportId" element={<ShellRoute contentClassName="p-0"><BimQuantityMappingPage /></ShellRoute>} />
              <Route path="/budget/:budgetId/review" element={<ShellRoute contentClassName="p-0"><BudgetReviewPage /></ShellRoute>} />
              <Route path="/budget/:budgetId/comparison" element={<ShellRoute contentClassName="p-0"><BudgetComparisonPage /></ShellRoute>} />
              <Route path="/budget/:budgetId/measurement" element={<ShellRoute contentClassName="p-0"><MeasurementPage /></ShellRoute>} />
              <Route path="/templates" element={<ShellRoute><Templates /></ShellRoute>} />
              <Route path="/templates/:templateId" element={<ShellRoute contentClassName="p-0"><TemplateDetail /></ShellRoute>} />
              <Route path="/bim" element={<ShellRoute><BimPage /></ShellRoute>} />
              <Route path="/bim/revit-import" element={<ShellRoute><RevitTakeoffImportPage /></ShellRoute>} />
              <Route path="/works" element={<ShellRoute contentClassName="p-0"><WorksPage /></ShellRoute>} />
              <Route path="/measurement-autos" element={<ShellRoute contentClassName="p-0"><MeasurementAutosIndexPage /></ShellRoute>} />
              <Route path="/documents" element={<ShellRoute contentClassName="p-0"><DocumentsPage /></ShellRoute>} />
              <Route path="/contacts" element={<ShellRoute contentClassName="p-0"><ContactsPage /></ShellRoute>} />
              <Route path="/invite/:token" element={<AcceptInvite />} />
              <Route path="/settings" element={<ShellRoute contentClassName="p-0"><Settings /></ShellRoute>} />
              <Route path="/cost-database" element={<ShellRoute contentClassName="p-0"><CostDatabasePage /></ShellRoute>} />
              <Route path="/cost-database/price-intelligence" element={<ShellRoute contentClassName="p-0"><PriceIntelligencePage /></ShellRoute>} />
              <Route path="/legal/termos/pt" element={<TermosPT />} />
              <Route path="/legal/termos/br" element={<TermosBR />} />
              <Route path="/legal/privacidade/pt" element={<PrivacidadePT />} />
              <Route path="/legal/privacidade/br" element={<PrivacidadeBR />} />
              <Route path="/legal/cookies" element={<LegalCookies />} />
              <Route path="/help" element={<ShellRoute><Help /></ShellRoute>} />
              <Route path="/admin/catalog" element={<AdminGuard><ShellRoute><AdminCatalogOverview /></ShellRoute></AdminGuard>} />
              <Route path="/admin" element={<AdminGuard><ShellRoute><AdminDashboard /></ShellRoute></AdminGuard>} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <CookieConsentBanner />
          <LocaleOnboardingDialog />
          <OrzioAssistant />
        </BrowserRouter>
      </TooltipProvider>
    </LanguageProvider>
  </QueryClientProvider>
);

export default App;
